import { startCase } from "lodash";
import { FC, MouseEventHandler, useMemo } from "react";
import { useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import useEvent from "react-use-event-hook";
import { useRecoilValue } from "recoil";

import {
  BookingApproveFormFieldsEnum,
  ErrorsDisplayContainer,
} from "@ryde/controls";
import { useCrudEditContext } from "@ryde/redux";
import {
  BookingStatusEnum,
  DrawerType,
  EditFormMode,
  errorsCrudContentAtomFamily,
  formTabControlsSF,
  IDrawersCommonProps,
  IExtendableDto,
  IInjectedDrawerProps,
  useDrawers,
} from "@ryde/shared";

import {
  EditFormTabs,
  FormControlsWrapper,
  useEditFormInner,
} from "../../../layout-edit";
import {
  BaseDrawer,
  CrudEditFormFooter,
  DrawerContentStyled,
  DrawerFormContentGate,
  DrawerHeaderWithSubtitleHistory,
} from "../drawer";
import { useEditFormDrawerLoading } from "../edit-form";
import { resolveBookingIneligibleErrors } from "./booking-edit.helpers";
import { BookingEditIneligibleApprove } from "./components";
import { useBookingEditEffects } from "./hooks";

export const BookingEditInner: FC<
  IDrawersCommonProps & IInjectedDrawerProps
> = ({ onCloseDrawer, resolveFn, drawerNumber }) => {
  const {
    data: { isEdit, isDuplicate, dto },
    entity,
    mode,
    id,
  } = useCrudEditContext<IExtendableDto>();
  const { openDrawer } = useDrawers();

  const { t } = useTranslation();
  const isAddNew = !isEdit;
  const unitTitle = t(`${entity}.single`);
  const secondaryTitle = t(`general.ADD_NEW`, { entity: unitTitle });
  const currentTabControls = useRecoilValue(formTabControlsSF(entity));
  const { isFormContentLoading, isFormFooterLoading } =
    useEditFormDrawerLoading({
      entity,
      requestedId: id,
      dtoId: dto?.id,
      hasDto: Boolean(dto),
    });

  const headerTitle = useMemo(() => {
    if (isEdit) {
      if (dto) {
        const entityName = dto.title || dto.name;
        const title = entityName ? `${unitTitle}: ${entityName}` : unitTitle;
        return startCase(title);
      }
      return startCase(unitTitle);
    }
    return startCase(secondaryTitle);
  }, [isEdit, dto, unitTitle, secondaryTitle]);
  const serverErrors = useRecoilValue(errorsCrudContentAtomFamily(entity));
  const { isIneligibleErrors } = useBookingEditEffects();

  const {
    formState: { isDirty, isValid, errors },
    watch,
  } = useFormContext();

  const {
    onDuplicateClick,
    onArchiveClick,
    closeSidebar,
    subHeaderTitle,
    onSaveClick,
  } = useEditFormInner({ onCloseDrawer, resolveFn });

  const approverValue = watch(BookingApproveFormFieldsEnum.APPROVER);
  const approverReasonValue = watch(
    BookingApproveFormFieldsEnum.APPROVAL_REASON_ID,
  );

  const isBookingRouted = dto?.status === BookingStatusEnum.Routed;
  const isApprovedIfNeed = isIneligibleErrors
    ? approverValue && approverReasonValue
    : true;
  const isSaveDisabled =
    !isApprovedIfNeed || !isValid || (!isDirty && !isDuplicate);
  const isArchiveDisabled =
    isAddNew || mode !== EditFormMode.CrudRoute || isBookingRouted;

  const onHistoryClick: MouseEventHandler<HTMLButtonElement> = useEvent(
    (e): void => {
      e.preventDefault();

      if (!dto?.id) {
        return;
      }

      openDrawer({
        drawerProps: {
          type: DrawerType.HistoryLog,
          entity,
          id: dto.id,
        },
      });
    },
  );

  return (
    <BaseDrawer onCloseDrawer={closeSidebar} drawerNumber={drawerNumber}>
      <DrawerFormContentGate
        isLoading={isFormContentLoading}
        entityTitle={unitTitle}
      >
        <DrawerHeaderWithSubtitleHistory
          onCloseDrawer={closeSidebar}
          title={headerTitle}
          subTitle={subHeaderTitle}
          onHistoryClick={
            isEdit && entity && entity !== "unknown"
              ? onHistoryClick
              : undefined
          }
        />
        <DrawerContentStyled className="drawer-form-with-image">
          <EditFormTabs>
            <ErrorsDisplayContainer
              serverErrors={resolveBookingIneligibleErrors(serverErrors, t)}
              formErrors={isValid ? undefined : errors}
              marginLeft="0"
            />
            {(isAddNew || !!dto) && (
              <FormControlsWrapper controls={currentTabControls} />
            )}
            {isIneligibleErrors && <BookingEditIneligibleApprove />}
          </EditFormTabs>
        </DrawerContentStyled>
        <CrudEditFormFooter
          entity={entity}
          onSaveClick={onSaveClick}
          onCancelClick={closeSidebar}
          isLoading={isFormFooterLoading}
          isSaveDisabled={isSaveDisabled}
          duplicateClick={isEdit ? onDuplicateClick : undefined}
          isDuplicateDisabled={isAddNew || mode !== EditFormMode.CrudRoute}
          archiveClick={isEdit ? onArchiveClick : undefined}
          isArchiveDisabled={isArchiveDisabled}
          deleteMessage={t(`general.ARCHIVE_MESSAGE`, { entity: unitTitle })}
          duplicationMessage={t(`general.DUPLICATE_MESSAGE`, {
            entity: unitTitle,
          })}
        />
      </DrawerFormContentGate>
    </BaseDrawer>
  );
};
